import { SessionInitialization } from "./features/auth/SessionInitialization";
import { Route, Router, Switch } from "wouter-preact";
import { PaymentLayout } from "./components/PaymentLayout";
import { ErrorPanel, LoadingPanel } from "./components/StatePanels";
import { SiteConfigProvider } from "./features/tenant/SiteConfigContext";
import { selectCurrentSiteConfig, selectLocalTenantHost } from "./features/tenant/tenantSelectors";
import { isInAppEnv, isLocalHost } from "./lib/env";
import { routePaths } from "./lib/route";
import { AccountPage } from "./pages/AccountPage";
import { AnonymousInvoicePage } from "./pages/AnonymousInvoicePage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { SearchPage } from "./pages/SearchPage";
import { LoginTestPage } from "./pages/LoginTestPage";
import { SignInPage } from "./pages/SignInPage";
import { PortalPage } from "./pages/PortalPage";
import { StyleGuidePage } from "./pages/StyleGuidePage";
import type { SiteConfig } from "./lib/gboModels";
import { useAppSelector } from "./store/hooks";
import { getApiFailureMessage, getApiFailureName, useGetSiteConfigQuery } from "./store/gboApi";

const localMode = isLocalHost();

export function App() {
  const localTenantHost = useAppSelector(selectLocalTenantHost);
  const currentSiteConfig = useAppSelector(selectCurrentSiteConfig);
  const siteConfigQuery = useGetSiteConfigQuery({ host: localMode ? localTenantHost : undefined });

  if (siteConfigQuery.isError || !currentSiteConfig) return renderSiteConfigGate(siteConfigQuery);

  return (
    <SiteConfigProvider siteConfig={currentSiteConfig}>
      {renderAppBody(currentSiteConfig.tenantId)}
    </SiteConfigProvider>
  );
}

type QueryResult<T> = {
  data?: T;
  error?: unknown;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
};

function renderSiteConfigGate(siteConfigQuery: QueryResult<SiteConfig>) {
  if (siteConfigQuery.isLoading || siteConfigQuery.isFetching)
    return <LoadingPanel title="Loading" />;

  if (siteConfigQuery.isError && getApiFailureName(siteConfigQuery.error) === "NotFoundError")
    return (
      <ErrorPanel
        title="Site not found"
        message={getApiFailureMessage(siteConfigQuery.error, "Site not found.")}
      />
    );

  if (siteConfigQuery.isError)
    return (
      <ErrorPanel
        title="Unable to load site"
        message={getApiFailureMessage(siteConfigQuery.error, "Unable to load site config.")}
      />
    );

  return <LoadingPanel title="Loading" />;
}

function renderAppBody(tenantId: string) {
  return (
    <Router>
      <Switch>
        <Route path={/^\/pay(?:\/.*)?$/}>
          <PaymentLayout>
            <Switch>
              <Route path={routePaths.search}>
                <SearchPage />
              </Route>
              <Route path={routePaths.accountDetail}>
                {({ customerCode }) => <AccountPage customerCode={customerCode} />}
              </Route>
              <Route path={routePaths.invoice}>
                {({ invoiceCode }) => <AnonymousInvoicePage invoiceCode={invoiceCode} />}
              </Route>
              <Route>
                <NotFoundPage />
              </Route>
            </Switch>
          </PaymentLayout>
        </Route>
        <Route>
          <SessionInitialization key={tenantId}>
            <Switch>
              <Route path={routePaths.portal.base} nest>
                <PortalPage />
              </Route>
              <Route>
                <PaymentLayout>
                  <Switch>
                    <Route path={routePaths.root}>
                      <SearchPage />
                    </Route>
                    {isInAppEnv("Local", "Development") && (
                      <Route path={routePaths.styleGuide}>
                        <StyleGuidePage />
                      </Route>
                    )}
                    <Route path={routePaths.loginTest}>
                      <LoginTestPage />
                    </Route>
                    <Route path={routePaths.signIn}>
                      <SignInPage />
                    </Route>
                    <Route>
                      <NotFoundPage />
                    </Route>
                  </Switch>
                </PaymentLayout>
              </Route>
            </Switch>
          </SessionInitialization>
        </Route>
      </Switch>
    </Router>
  );
}
